import { mapDesigns } from "@mono/mapDesigns"
import classNames from "classnames"
import Money from "./Money"
import cardHouseIcon from "@/images/house-card.webp"
import { allMapsImageData, images } from "@/utils/data"
import { useMemo } from "react"
import { useAppSelector } from "@/store/hooks"

// Matches the rent multiplier applied to the "rent-lord" role in calculateRent
const rentLordMultiplier = 1.5

// Transport cards carry one column less than property cards, but keep the same
// rhythm: a narrow label column followed by the two rent columns.
const transportCols = "grid-cols-[1fr_1fr_1.05fr]"

type CardSize = "normal" | "mobile-big" | "auction-modal" | "small" | "wide"

interface SizeStyle {
  card: string
  pad: string
  band: string
  title: string
  text: string
  rent: string
  rentLord: string
  head: string
  headLord: string
  value: string
  valueLord: string
  icon: string
  cols: string
  colGap: string
  row: string
  gap: string
  image: string
}

// Every variant keeps the same proportions: type, colour band and row rhythm
// all scale together with the card width, so the card reads identically at any
// size instead of a fixed table squeezed into a different width. The building
// column is the narrowest one - it only holds the house icon and an "xN", so
// the icon is kept small to leave the money columns their room.
const sizeStyles: Record<CardSize, SizeStyle> = {
  normal: {
    card: "w-[19rem]",
    pad: "px-5 py-5",
    band: "min-h-[4.5rem]",
    title: "text-xl",
    text: "text-base",
    rent: "text-3xl",
    rentLord: "text-base",
    head: "text-sm",
    headLord: "text-xs",
    value: "text-base",
    valueLord: "text-sm",
    icon: "h-[0.95rem] w-[0.95rem]",
    cols: "grid-cols-[0.85fr_1fr_1fr_1.05fr]",
    colGap: "gap-2",
    row: "py-3.5",
    gap: "mt-5",
    image: "h-20",
  },
  wide: {
    card: "w-[20rem]",
    pad: "px-6 py-5",
    band: "min-h-[4.75rem]",
    title: "text-2xl",
    text: "text-lg",
    rent: "text-3xl",
    rentLord: "text-base",
    head: "text-sm",
    headLord: "text-xs",
    value: "text-base",
    valueLord: "text-sm",
    icon: "h-[1rem] w-[1rem]",
    cols: "grid-cols-[0.85fr_1fr_1fr_1.05fr]",
    colGap: "gap-2",
    row: "py-3.5",
    gap: "mt-5",
    image: "h-24",
  },
  "auction-modal": {
    card: "w-[19rem]",
    pad: "px-5 py-5",
    band: "min-h-[4.5rem]",
    title: "text-xl",
    text: "text-base",
    rent: "text-3xl",
    rentLord: "text-base",
    head: "text-sm",
    headLord: "text-xs",
    value: "text-base",
    valueLord: "text-sm",
    icon: "h-[0.95rem] w-[0.95rem]",
    cols: "grid-cols-[0.85fr_1fr_1fr_1.05fr]",
    colGap: "gap-2",
    row: "py-3.5",
    gap: "mt-5",
    image: "h-20",
  },
  small: {
    card: "w-[14.5rem]",
    pad: "px-4 py-4",
    band: "min-h-[3.4rem]",
    title: "text-base",
    text: "text-sm",
    rent: "text-2xl",
    rentLord: "text-xs",
    head: "text-xs",
    headLord: "text-[0.6rem]",
    value: "text-xs",
    valueLord: "text-[0.7rem]",
    icon: "h-[0.75rem] w-[0.75rem]",
    cols: "grid-cols-[0.85fr_1fr_1fr_1.05fr]",
    colGap: "gap-1.5",
    row: "py-2.5",
    gap: "mt-4",
    image: "h-16",
  },
  // Rendered on phones, where the root font size is halved (1rem = 8px) but the
  // card keeps a fixed pixel width - so every rem step here is roughly double
  // the normal one. Sized to the SHORT side of a landscape phone: the whole
  // card has to clear ~360px of viewport height once the modal's close-button
  // gutter is taken out, so it runs ~265px tall rather than filling the width.
  "mobile-big": {
    card: "w-[230px]",
    pad: "px-[1.5rem] py-[1.25rem]",
    band: "min-h-[5rem]",
    title: "text-[1.6rem]",
    text: "text-[1.3rem]",
    rent: "text-[2.25rem]",
    rentLord: "text-[1.3rem]",
    head: "text-[1.15rem]",
    headLord: "text-[0.95rem]",
    value: "text-[1.3rem]",
    valueLord: "text-[1.15rem]",
    icon: "h-[1.2rem] w-[1.2rem]",
    cols: "grid-cols-[0.85fr_1fr_1fr_1.05fr]",
    colGap: "gap-[0.65rem]",
    row: "py-[0.9rem]",
    gap: "mt-[1.25rem]",
    image: "h-[6.5rem]",
  },
}

interface PropertyBidCardProps {
  cell: ICell
  size?: CardSize
  label?: "sold" | "auctioned"
}

const PropertyBidCard: React.FC<PropertyBidCardProps> = ({
  cell,
  size = "normal",
  label,
}) => {
  const myGame = useAppSelector((state) => state.global.myGame)
  const myMap = mapDesigns.find((map) => map.id === myGame?.settings.mapId)
  const myGroup = myMap?.groups.find(
    (group) => group.id === cell?.propertyDetails?.groupId
  )

  const s = sizeStyles[size]

  const labelStamp = useMemo(() => {
    if (!label) return null
    if (label === "sold") {
      return (
        <img
          src={images.icons.sold}
          className=" w-28 object-cover absolute top-0 left-0"
        ></img>
      )
    }
    if (label === "auctioned") {
      return (
        <img
          src={images.icons.auctioned}
          className=" w-28 object-cover absolute top-0 left-0"
        ></img>
      )
    }
    const isDefaultColoredHeader =
      myGroup && myGroup.id !== "8" && myGroup.id !== "9"
    // For default colored headers, place slightly outside the top-left corner to avoid covering the header content
    // For image/utility variants, keep at top-right inside the safe margin
    const positionClasses = isDefaultColoredHeader
      ? "top-1 left-1"
      : "top-0 left-0"
    const baseClasses = [
      "absolute",
      positionClasses,
      "z-20",
      "-rotate-6",
      "px-1 py-1",
      "rounded-md",
      "border-[6px]",
      "font-bebas font-extrabold uppercase tracking-wider  text-3xl",
      "pointer-events-none",
    ].join(" ")
    const colorClasses =
      label === "sold"
        ? "text-white border-green-500 "
        : "text-red-600 border-red-500"
    return (
      <div
        className={`${baseClasses} ${colorClasses}`}
        style={{
          textShadow: "0 0 5px rgba(0, 0, 0, 0.25)",
        }}
      >
        {label}
      </div>
    )
  }, [label, myGroup])
  if (!cell) return null

  if (myGroup?.id === "8") {
    // The "Owned" column header carries the meaning, so each row only needs the
    // count - that frees the width the rent lord column needs.
    const transportRents = [
      { count: 1, rent: 25 },
      { count: 2, rent: 50 },
      { count: 3, rent: 100 },
      { count: 4, rent: 200 },
    ]

    return (
      <div
        data-testid={`game.property-big-card-${cell.order}`}
        className={classNames(
          "relative flex bg-white shadow-xl rounded-xl overflow-hidden text-black pointer-events-none",
          s.card
        )}
      >
        {labelStamp}
        <div className={classNames("flex flex-col w-full", s.pad)}>
          <div
            className={classNames(
              "flex justify-center items-center text-center px-3 py-2 w-full",
              s.band
            )}
          >
            <div
              className={classNames(
                "text-black font-raleway uppercase font-bold leading-tight tracking-wide break-words",
                s.title
              )}
            >
              {cell.propertyDetails?.name}
            </div>
          </div>

          <div className={classNames("flex flex-col", s.gap)}>
            <div
              className={classNames(
                "font-poppins text-gray-600 leading-snug",
                s.text
              )}
            >
              This set includes Airport, Dock, Bus Stand and Railway Station
            </div>

            <div className={classNames("flex flex-col", s.gap)}>
              {/* Column Headers */}
              <div
                className={classNames(
                  "grid items-baseline border-b border-propertyCard-divider pb-1.5 font-poppins font-semibold text-gray-700 tracking-wide",
                  transportCols,
                  s.colGap,
                  s.head
                )}
              >
                <div>Owned</div>
                <div>Rent</div>
                <div className={classNames("whitespace-nowrap", s.headLord)}>
                  Rent Lord
                </div>
              </div>

              {transportRents.map((item, index, arr) => (
                <div
                  key={item.count}
                  className={classNames(
                    "grid items-center",
                    transportCols,
                    s.colGap,
                    s.row,
                    {
                      "border-b border-propertyCard-divider":
                        index !== arr.length - 1,
                    }
                  )}
                >
                  <div
                    className={classNames(
                      "font-poppins text-gray-900 font-semibold",
                      s.value
                    )}
                  >
                    x{item.count}
                  </div>
                  <Money
                    money={item.rent}
                    className={classNames(
                      "font-poppins font-semibold text-gray-900 gap-0.5",
                      s.value
                    )}
                    customFont="font-raleway font-bold"
                  />
                  <Money
                    money={Math.floor(item.rent * rentLordMultiplier)}
                    className={classNames(
                      "font-poppins font-medium text-gray-900 gap-0.5",
                      s.valueLord
                    )}
                    customFont="font-raleway font-semibold"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (myGroup?.id === "9") {
    const imagesOfMyMapId = allMapsImageData.find(
      (item) => item.mapId === myGame?.settings.mapId
    )
    const myImageData = imagesOfMyMapId?.cellImages.find(
      (item) => item.order === cell.order
    )

    return (
      <div
        data-testid={`game.property-big-card-${cell.order}`}
        className={classNames(
          "relative flex bg-white shadow-xl rounded-xl overflow-hidden text-black pointer-events-none",
          s.card
        )}
      >
        {labelStamp}
        <div
          className={classNames(
            "flex flex-col items-center w-full justify-center",
            s.pad
          )}
        >
          <img
            className={classNames("w-auto", s.image)}
            src={myImageData?.bigCardImage}
          />

          <div
            className={classNames(
              "text-black font-raleway uppercase font-bold tracking-wide text-center leading-tight break-words",
              s.title,
              s.gap
            )}
          >
            {cell.propertyDetails?.name}
          </div>
          <div
            className={classNames(
              "text-center text-gray-700 leading-relaxed font-poppins",
              s.text,
              s.gap
            )}
          >
            If one Utility is owned, rent is 4 times amount shown on dice.
            <br />
            <br />
            If both Utilities are owned, rent is 10 times amount shown on dice.
          </div>
        </div>
      </div>
    )
  }

  const baseRent = cell.propertyDetails?.rentData?.base || 0
  const upgradeItems = cell.propertyDetails?.rentData?.upgradeItems || []

  return (
    <div
      data-testid={`game.property-big-card-${cell?.order}`}
      className={classNames(
        "relative flex bg-white shadow-xl rounded-xl overflow-hidden text-black pointer-events-none",
        s.card
      )}
    >
      {labelStamp}
      <div className={classNames("flex flex-col w-full", s.pad)}>
        {/* Colour band - hugs the name so long ones wrap instead of clipping */}
        <div
          className={classNames(
            "flex justify-center items-center text-center w-full px-3 py-2 shadow rounded-md",
            s.band
          )}
          style={{ backgroundColor: myGroup?.color }}
        >
          <div
            className={classNames(
              "text-white font-raleway uppercase font-bold leading-tight tracking-wide break-words",
              s.title
            )}
          >
            {cell.propertyDetails?.name}
          </div>
        </div>

        {/* Base rent */}
        <div className={classNames("flex flex-col items-center", s.gap)}>
          <div
            className={classNames(
              "flex gap-2 items-baseline font-poppins justify-center font-semibold tracking-wide text-gray-900",
              s.rent
            )}
          >
            <div>Rent</div>
            <Money
              className="gap-0.5"
              money={baseRent}
              customFont="font-raleway font-bold"
            ></Money>
          </div>
          <div
            className={classNames(
              "flex gap-1.5 items-baseline font-poppins justify-center font-medium tracking-wide text-gray-900",
              s.rentLord
            )}
          >
            <div>Rent Lord</div>
            <Money
              className="gap-0.5"
              money={Math.floor(baseRent * rentLordMultiplier)}
              customFont="font-raleway font-semibold"
            ></Money>
          </div>
        </div>

        <div className={classNames("flex flex-col", s.gap)}>
          {/* Column Headers */}
          <div
            className={classNames(
              "grid items-baseline border-b border-propertyCard-divider pb-1.5 font-poppins font-semibold text-gray-700 tracking-wide",
              s.cols,
              s.colGap,
              s.head
            )}
          >
            <div>House</div>
            <div>Price</div>
            <div>Rent</div>
            <div className={classNames("whitespace-nowrap", s.headLord)}>
              Rent Lord
            </div>
          </div>

          {/* Upgrade Items */}
          {upgradeItems.map((item, index, arr) => (
            <div
              key={item.count}
              className={classNames(
                "grid items-center",
                s.cols,
                s.colGap,
                s.row,
                {
                  "border-b border-propertyCard-divider":
                    index !== arr.length - 1,
                }
              )}
            >
              <div className="flex items-center gap-1">
                <img
                  src={cardHouseIcon}
                  className={classNames("drop-shadow-2xl", s.icon)}
                />
                <span
                  className={classNames(
                    "font-poppins text-gray-900 font-semibold",
                    s.value
                  )}
                >
                  x{item.count}
                </span>
              </div>
              <Money
                money={item.price}
                className={classNames(
                  "text-gray-900 font-poppins font-semibold gap-0.5",
                  s.value
                )}
                customFont="font-raleway font-bold"
              />
              <Money
                money={item.rent}
                className={classNames(
                  "text-gray-900 font-poppins font-semibold gap-0.5",
                  s.value
                )}
                customFont="font-raleway font-bold"
              />
              <Money
                money={Math.floor(item.rent * rentLordMultiplier)}
                className={classNames(
                  "text-gray-900 font-poppins font-medium gap-0.5",
                  s.valueLord
                )}
                customFont="font-raleway font-semibold"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default PropertyBidCard
